import { useState, useEffect } from 'react';
import { ShopifyProduct } from './useSelectiveShopify';
import { productionLogger } from '@/utils/production-logger';

interface RecentlyViewedItem {
  id: string;
  title: string;
  handle: string;
  price: string;
  image?: string;
  viewedAt: number;
}

export const useRecentlyViewed = () => {
  const STORAGE_KEY = 'recently-viewed-products';
  const MAX_ITEMS = 20;
  
  // React state for immediate reactivity
  const [recentlyViewed, setRecentlyViewed] = useState<RecentlyViewedItem[]>([]);
  
  // Use localStorage directly and sync with state
  const getStoredItems = (): RecentlyViewedItem[] => {
    // CRITICAL: Check both window and localStorage exist (prevents SSR/hydration crash)
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return [];
    }
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      productionLogger.error('Error reading recently viewed from localStorage:', error);
      return [];
    }
  };
  
  const setStoredItems = (items: RecentlyViewedItem[]) => {
    // CRITICAL: Check both window and localStorage exist (prevents SSR/hydration crash)
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      setRecentlyViewed(items); // Update state even if can't persist
      return;
    }
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      setRecentlyViewed(items); // Update React state for immediate reactivity
    } catch (error) {
      productionLogger.error('Error saving recently viewed to localStorage:', error);
    }
  };

  // Load initial data from localStorage on mount
  useEffect(() => {
    setRecentlyViewed(getStoredItems());
  }, []);

  const addToRecentlyViewed = (product: ShopifyProduct) => {
    const current = getStoredItems();
    
    // Remove if already exists to avoid duplicates
    const filtered = current.filter(item => item.id !== product.id);
    
    // Add to beginning of array
    const updated = [{
      id: product.id,
      title: product.title,
      handle: product.handle,
      price: product.priceRange?.minVariantPrice?.amount || product.variants?.[0]?.price?.amount || '0',
      image: product.images?.[0]?.url,
      viewedAt: Date.now()
    }, ...filtered];
    
    // Keep only last MAX_ITEMS items
    const trimmed = updated.slice(0, MAX_ITEMS);
    
    setStoredItems(trimmed);
  };

  const getRecentlyViewed = (): RecentlyViewedItem[] => {
    return recentlyViewed; // Return from React state for reactivity
  };

  const clearRecentlyViewed = () => {
    setStoredItems([]);
  };

  const removeFromRecentlyViewed = (productId: string) => {
    const current = getStoredItems();
    const filtered = current.filter(item => item.id !== productId);
    setStoredItems(filtered);
  };

  const getRecentlyViewedStats = () => {
    const items = getRecentlyViewed();
    return {
      total: items.length,
      lastViewed: items[0]?.viewedAt || null,
      oldestViewed: items[items.length - 1]?.viewedAt || null
    };
  };

  return {
    addToRecentlyViewed,
    getRecentlyViewed,
    clearRecentlyViewed,
    removeFromRecentlyViewed,
    getRecentlyViewedStats
  };
};
